'use client';

import { updateCart } from '@/api/cart';
import { ICartItem } from '@/api/types/cart';
import Button from '@/components/Button';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { handlerPrescriptionData } from '@/utils/prescription';
import { ClickAwayListener, Fade, Paper, Popper } from '@mui/material';
import Image from '@/components/Image';
import { useEffect, useState } from 'react';
import { useCartSensorsTracking } from '../../hooks/useCartSensorsTracking';
import { useTranslations } from '@/i18n/use-translations';

// 类型定义
interface ColorChangePopupProps {
  cartItem: ICartItem;
  open: boolean;
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  loading: boolean;
  onReload?: (cartData?: any, modifiedCartId?: number) => void;
}

export default function ColorChangePopup({
  cartItem,
  open,
  anchorEl,
  onClose,
  loading,
  onReload,
}: ColorChangePopupProps) {
  const t = useTranslations('cart.ColorChangePopup');
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [internalLoading, setInternalLoading] = useState(false);

  // 从store获取setCart方法
  const setCart = useStore((state) => state.setCart);

  // 直接使用埋点hook
  const { trackCartFunctionBtnClick } = useCartSensorsTracking();

  // 获取可选颜色列表
  const getColorOptions = () => {
    const sameColorSpu = cartItem?.product?.same_color_spu || [];
    return sameColorSpu;
  };

  // 弹窗打开时，默认选中当前商品
  useEffect(() => {
    if (open && cartItem?.product?.same_color_spu) {
      const currentColorItem = cartItem.product.same_color_spu.find(
        (item) => item.product_id === cartItem.product_id,
      );
      if (currentColorItem) {
        setSelectedColor(currentColorItem);
      }
    }
  }, [open, cartItem]);

  // 选择颜色
  const handleColorSelect = (colorOption: any) => {
    setSelectedColor(colorOption);
  };

  // 判断是否为灰底图
  const isGrayImage = (colorItem: any) => {
    return !!(colorItem as any).img_gray;
  };

  // 确认更换颜色
  const handleConfirm = async () => {
    // 🔍 直接调用神策埋点 - 购物车功能按钮点击
    trackCartFunctionBtnClick('Color');

    // 如果选中的是当前商品，直接关闭弹窗
    if (selectedColor?.product_id === cartItem.product_id) {
      onClose();
      return;
    }
    if (!selectedColor) return;

    try {
      setInternalLoading(true);

      // 使用handlerPrescriptionData方法整合数据
      const params = handlerPrescriptionData({
        cart_id: cartItem.cart_id,
        product_id: selectedColor.product_id,
        qty: cartItem.qty,
        option: {
          ...cartItem.option,
        },
        prescriptions_image: cartItem.prescriptions_image || '',
      });

      // 调用更新购物车接口
      const response = await updateCart(params);
      // 关闭弹窗
      onClose();
      // 使用onReload回调或者直接调用setCart方法
      if (response?.data) {
        if (onReload) {
          onReload(response.data, cartItem.cart_id);
        } else {
          await setCart(response.data);
        }
      }
    } catch (error) {
      logger.error('Failed to change color:', error);
    } finally {
      setInternalLoading(false);
    }
  };

  const colorOptions = getColorOptions();

  // 如果没有可选颜色，不显示弹窗
  if (colorOptions.length === 0) {
    return null;
  }

  // PC端渲染
  const renderPcVersion = () => (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <div>
            <ClickAwayListener onClickAway={onClose}>
              <Paper
                elevation={3}
                sx={{
                  padding: '0 20px 24px',
                  borderRadius: 2,
                  boxShadow: '0 4px 16px 0 rgb(0 0 0 / 16%)',
                  border: '1px solid #dce1e5',
                  minWidth: 480,
                  backgroundColor: 'white',
                }}
              >
                <div className="color-select-wrap font-br">
                  {/* 标题栏 */}
                  <div className="flex items-center justify-center pt-[32px]">
                    <h4 className="text-[20px] font-bb text-primary-black">{t('selectColor')}</h4>
                    <button
                      onClick={onClose}
                      className="text-[16px] text-secondary-black cursor-pointer hover:text-primary-black absolute right-[14px] top-[14px]"
                    >
                      ✕
                    </button>
                  </div>

                  {/* 颜色网格 - 动态布局 */}
                  <div
                    className={`mt-[20px] mb-[20px] ${
                      colorOptions.length > 4
                        ? 'grid grid-cols-5 gap-x-0 gap-y-[30px]'
                        : 'flex justify-center gap-x-[20px] gap-y-[30px] flex-wrap'
                    }`}
                  >
                    {colorOptions.map((colorOption) => (
                      <div
                        key={colorOption.product_id}
                        className="flex flex-col items-center cursor-pointer"
                        onClick={() => handleColorSelect(colorOption)}
                      >
                        <div
                          className={`relative w-[80px] h-[80px] bg-[#eeeeee] border border-[#dce1e5] rounded-[5px] flex items-center justify-center ${
                            selectedColor?.product_id === colorOption.product_id
                              ? 'border-primary-purple border-1'
                              : ''
                          }`}
                        >
                          <Image
                            src={(colorOption as any).img_gray || colorOption.img}
                            alt={colorOption.color}
                            width={66}
                            height={66}
                            className="object-contain"
                            style={!isGrayImage(colorOption) ? { mixBlendMode: 'multiply' } : {}}
                          />
                          {/* HOT 标签 */}
                          {colorOption.is_hot === 1 && (
                            <span
                              className={`absolute top-0 right-0 bg-[#fa1261] text-white text-[12px] px-[7px] h-[20px] leading-[20px] rounded-[0px_6px_0px_6px] ${
                                selectedColor?.product_id === colorOption.product_id
                                  ? 'top-[-2px] right-[-2px]'
                                  : ''
                              }`}
                            >
                              {t('hot')}
                            </span>
                          )}
                          {/* New In 标签 -- 优先级比 HOT 低 */}
                          {(colorOption as any).is_new_in === 1 && colorOption.is_hot !== 1 && (
                            <span
                              className={`absolute top-0 right-0 bg-[#454fed] text-white text-[12px] px-[7px] h-[20px] leading-[20px] rounded-[0px_6px_0px_6px] ${
                                selectedColor?.product_id === colorOption.product_id
                                  ? 'top-[-2px] right-[-2px]'
                                  : ''
                              }`}
                            >
                              {t('newIn')}
                            </span>
                          )}
                        </div>
                        <div
                          className={`text-[12px] text-center mt-2 w-full truncate ${
                            selectedColor?.product_id === colorOption.product_id
                              ? 'text-primary-purple font-bs'
                              : 'text-primary-black'
                          }`}
                        >
                          {colorOption.color}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 确认按钮 */}
                  <Button
                    onClick={handleConfirm}
                    disabled={loading || internalLoading}
                    loading={loading || internalLoading}
                    radiusSize="small"
                    variant="contained"
                    className="!font-bs"
                    sx={{
                      width: '286px',
                      height: '40px',
                      margin: '0 auto',
                      display: 'block',
                    }}
                  >
                    {t('confirm')}
                  </Button>
                </div>
              </Paper>
            </ClickAwayListener>
          </div>
        </Fade>
      )}
    </Popper>
  );

  // 移动端渲染
  const renderMobileVersion = () => (
    <Popup
      open={open}
      onClose={onClose}
      title={t('selectColor')}
      position="bottom"
      round
      height="auto"
      sx={{
        '& .MuiDrawer-paper': {
          borderRadius: '20px 20px 0 0',
        },
        '& .popup-content': {
          padding: '0',
        },
      }}
    >
      <div className="">
        {/* 颜色列表 - 4列布局 */}
        <div className="relative max-h-[60vh] overflow-y-auto box-border p-[14px] pb-[20px]">
          <div className="grid grid-cols-4 gap-x-[8px] gap-y-[20px]">
            {colorOptions.map((colorOption) => (
              <div
                key={colorOption.product_id}
                className={`flex flex-col items-center relative pb-[20px]`}
                onClick={() => handleColorSelect(colorOption)}
              >
                <div
                  className={`w-[100%] aspect-square box-border border border-[#dce1e5] rounded-[8px] bg-[#eeeeee] flex items-center justify-center ${
                    selectedColor?.product_id === colorOption.product_id
                      ? 'border-primary-purple'
                      : ''
                  }`}
                >
                  <Image
                    src={(colorOption as any).img_gray || colorOption.img}
                    alt={colorOption.color}
                    width={66}
                    height={66}
                    className="object-contain h-[auto] w-[80%]"
                    style={!isGrayImage(colorOption) ? { mixBlendMode: 'multiply' } : {}}
                  />
                  {/* HOT 标签 */}
                  {colorOption.is_hot === 1 && (
                    <span className="absolute top-0 right-0 bg-[#fa1261] text-white text-[11px] px-[6px] h-[15px] leading-[16px] rounded-[0px_8px_0px_8px]">
                      {t('hot')}
                    </span>
                  )}
                  {/* New In 标签 -- 优先级比 HOT 低 */}
                  {(colorOption as any).is_new_in === 1 && colorOption.is_hot !== 1 && (
                    <span className="absolute top-0 right-0 bg-[#454fed] text-white text-[11px] px-[6px] h-[15px] leading-[16px] rounded-[0px_8px_0px_8px]">
                      {t('newIn')}
                    </span>
                  )}
                </div>
                <span
                  className={`text-[12px] absolute left-1/2 transform -translate-x-1/2 bottom-0 w-full text-center truncate ${
                    selectedColor?.product_id === colorOption.product_id
                      ? 'text-primary-purple font-bs'
                      : 'text-primary-black'
                  }`}
                >
                  {colorOption.color}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 确认按钮 */}
        <div className="h-[69px] shadow-[0px_0px_10px_0px_rgba(204,204,204,0.5)] flex items-center justify-center">
          <Button
            onClick={handleConfirm}
            disabled={loading || internalLoading}
            loading={loading || internalLoading}
            radiusSize="large"
            variant="contained"
            className="!font-bs"
            sx={{
              width: '340px',
              height: '42px',
              fontSize: '16px',
              border: 0,
              '&:hover': {
                backgroundColor: '#5B5CE6',
              },
              '&:disabled': {
                opacity: 0.2,
                cursor: 'not-allowed',
              },
            }}
          >
            {t('confirm')}
          </Button>
        </div>
      </div>
    </Popup>
  );

  return (
    <>
      <WebOnly>{renderPcVersion()}</WebOnly>
      <WapOnly>{renderMobileVersion()}</WapOnly>
    </>
  );
}
